import { useMutation, useQueries, useQuery, useQueryClient } from '@tanstack/react-query'
import * as collabApi from './api'
import { getHaloUser } from '@/features/halocrm-user/api'
import { haloUserKeys } from '@/features/halocrm-user/hooks'
import type {
  CreateThreadInput,
  EditMessageInput,
  PostMessageInput,
  ThreadStatus,
} from './types'

// Query hooks

export function useThreads(entityRef: string, status?: ThreadStatus) {
  return useQuery({
    queryKey: ['collab', 'threads', entityRef, status ?? null],
    queryFn: () => collabApi.listThreads(entityRef, status),
  })
}

export function useMessages(threadId: number | null, live = false) {
  return useQuery({
    queryKey: ['collab', 'messages', threadId],
    queryFn: () => collabApi.listMessages(threadId!),
    enabled: threadId != null,
    // Polling tylko gdy brak realtime; przy aktywnym WS odswiezamy przez invalidacje.
    refetchInterval: live ? false : 7000,
  })
}

export function useMyMentions() {
  return useQuery({
    queryKey: ['collab', 'mentions'],
    queryFn: collabApi.myMentions,
  })
}

export function useUnread(entityRef: string) {
  return useQuery({
    queryKey: ['collab', 'unread', entityRef],
    queryFn: () => collabApi.unread(entityRef),
  })
}

/**
 * Rozwiązuje zbiór `authorUserId` (oraz id wzmianek) na `fullName` przez
 * lokalny katalog halo (`/api/halocrm-users/{id}`, cache 5 min). collab
 * przechowuje tylko numeryczne id, a proxy easy nie wzbogaca `authorName`,
 * więc nazwy doklejamy na froncie. Zwraca mapę id -> fullName (bez wpisu
 * dla id, których jeszcze nie pobrano / nie znaleziono).
 */
export function useUserNames(ids: number[]): Map<number, string> {
  const unique = Array.from(new Set(ids.filter(id => id > 0))).sort((a, b) => a - b)
  const results = useQueries({
    queries: unique.map(id => ({
      queryKey: haloUserKeys.detail(id),
      queryFn: () => getHaloUser(id),
      staleTime: 5 * 60_000,
    })),
  })
  const map = new Map<number, string>()
  unique.forEach((id, i) => {
    const name = results[i]?.data?.fullName
    if (name) map.set(id, name)
  })
  return map
}

export function useUserSearch(q: string) {
  return useQuery({
    queryKey: ['collab', 'users', q],
    queryFn: () => collabApi.searchUsers(q),
    enabled: q.trim().length >= 2,
  })
}

export function useUploadAttachment() {
  return useMutation({
    mutationFn: (file: File) => collabApi.uploadAttachment(file),
  })
}

// Mutation hooks

export function useCreateThread() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (input: CreateThreadInput) => collabApi.createThread(input),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['collab', 'threads'] })
    },
  })
}

export function usePostMessage() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: ({ threadId, input }: { threadId: number; input: PostMessageInput }) =>
      collabApi.postMessage(threadId, input),
    onSuccess: (_data, { threadId }) => {
      qc.invalidateQueries({ queryKey: ['collab', 'messages', threadId] })
      qc.invalidateQueries({ queryKey: ['collab', 'unread'] })
      qc.invalidateQueries({ queryKey: ['collab', 'threads'] })
    },
  })
}

export function useEditMessage() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: ({ messageId, input }: { messageId: number; input: EditMessageInput }) =>
      collabApi.editMessage(messageId, input),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['collab', 'messages'] })
    },
  })
}

export function useDeleteMessage() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (messageId: number) => collabApi.deleteMessage(messageId),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['collab', 'messages'] })
    },
  })
}

export function useSetThreadStatus() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: ({ threadId, status }: { threadId: number; status: ThreadStatus }) =>
      collabApi.setThreadStatus(threadId, status),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['collab', 'threads'] })
    },
  })
}

export function useInviteParticipants() {
  return useMutation({
    mutationFn: ({ threadId, userIds }: { threadId: number; userIds: number[] }) =>
      collabApi.inviteParticipants(threadId, userIds),
  })
}

export function useRemoveParticipant() {
  return useMutation({
    mutationFn: ({ threadId, userId }: { threadId: number; userId: number }) =>
      collabApi.removeParticipant(threadId, userId),
  })
}

export function useMarkRead() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: ({ threadId, lastReadMessageId }: { threadId: number; lastReadMessageId: number }) =>
      collabApi.markRead(threadId, lastReadMessageId),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['collab', 'unread'] })
    },
  })
}
